import React, { useEffect, useState } from "react";
import { getSiteSettings, saveSiteSettings } from "../../api/siteSettingsService";
import FullPageLoader from "../../common/FullPageLoader";
import SiteSettingsView from "./SiteSettingsView";

const DEFAULT_SETTINGS = {
  recaptchaEnable: false,
  verifyOrderEnable: false,
  siteLogo: "",
};

const getSettingsFromResponse = (response) =>
  response?.data?.settings ||
  response?.data?.siteSettings ||
  response?.data?.data ||
  response?.data ||
  response?.settings ||
  response?.siteSettings ||
  response ||
  {};

const toBoolean = (value) => value === true || value === 1 || value === "true";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read the site logo."));
    reader.readAsDataURL(file);
  });

const SiteSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [enableRecaptcha, setEnableRecaptcha] = useState(DEFAULT_SETTINGS.recaptchaEnable);
  const [verifyOrder, setVerifyOrder] = useState(DEFAULT_SETTINGS.verifyOrderEnable);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        setLoadError("");
        const response = await getSiteSettings();
        const data = getSettingsFromResponse(response);
        const nextSettings = {
          recaptchaEnable: toBoolean(data?.recaptchaEnable),
          verifyOrderEnable: toBoolean(data?.verifyOrderEnable),
          siteLogo: data?.siteLogoUrl || data?.siteLogo || "",
        };

        setSettings(nextSettings);
        setEnableRecaptcha(nextSettings.recaptchaEnable);
        setVerifyOrder(nextSettings.verifyOrderEnable);
      } catch (error) {
        setLoadError(
          error?.response?.data?.message || error?.message || "Failed to load site settings.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSiteSettings();
  }, []);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const uploadedLogo = logoFile ? await readFileAsDataUrl(logoFile) : "";
      const response = await saveSiteSettings({
        recaptchaEnable: enableRecaptcha,
        verifyOrderEnable: verifyOrder,
        siteLogo: uploadedLogo,
      });
      setSettings({
        recaptchaEnable: enableRecaptcha,
        verifyOrderEnable: verifyOrder,
        siteLogo: uploadedLogo || settings.siteLogo,
      });
      window.dispatchEvent(
        new CustomEvent("site-settings-updated", {
          detail: { siteLogo: uploadedLogo || settings.siteLogo },
        }),
      );
      setLogoFile(null);
      setEditing(false);
      setSuccessMessage(response?.message || "Site settings saved successfully.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || "Failed to save site settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setEnableRecaptcha(settings.recaptchaEnable);
    setVerifyOrder(settings.verifyOrderEnable);
    setLogoFile(null);
    setLogoPreview("");
    setErrorMessage("");
    setSuccessMessage("");
    setEditing(true);
  };

  if (loading) {
    return <FullPageLoader text="Loading site settings..." />;
  }

  if (!editing) {
    return (
      <>
        <SiteSettingsView settings={settings} onEdit={handleEdit} />
      </>
    );
  }

  return (
    <div className="container-fluid p-4">
      <form className="card p-4" onSubmit={handleSave}>
        <div className="row">
          <div className="col-lg-6 mb-4">
            <label className="form-label fw-semibold" htmlFor="site-logo">
              Site logo
            </label>
            <input
              id="site-logo"
              type="file"
              accept="image/*"
              className="form-control"
              onChange={(event) => setLogoFile(event.target.files?.[0] || null)}
            />
            {!logoPreview && settings.siteLogo && (
              <div className="mt-3">
                <img
                  src={settings.siteLogo}
                  alt="Current site logo"
                  className="img-thumbnail"
                  style={{ maxWidth: 240, maxHeight: 140, objectFit: "contain" }}
                />
              </div>
            )}
            {logoPreview && (
              <div className="mt-3">
                <img
                  src={logoPreview}
                  alt="Selected site logo preview"
                  className="img-thumbnail"
                  style={{ maxWidth: 240, maxHeight: 140, objectFit: "contain" }}
                />
              </div>
            )}
          </div>

          <div className="col-12">
            <div className="form-check form-switch mb-3">
              <input
                id="enable-recaptcha"
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={enableRecaptcha}
                onChange={(event) => setEnableRecaptcha(event.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="enable-recaptcha">
                Enable reCAPTCHA
              </label>
            </div>

            <div className="form-check form-switch">
              <input
                id="verify-order"
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={verifyOrder}
                onChange={(event) => setVerifyOrder(event.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="verify-order">
                Verify order
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            className="btn"
            style={{
              backgroundColor: "#032B4D",
              color: "#fff",
            }}
            onClick={() => {
              setEditing(false);
              setErrorMessage("");
              setLogoFile(null);
            }}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;
